/* eslint-disable */
import {
  Grid,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Autocomplete,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  IconButton,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  Chip,
  InputAdornment
} from '@material-ui/core';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useIsFeatureFlagEnabled } from '../../../utils/FeatureFlags';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddIcon from '@mui/icons-material/Add';
import PropTypes from 'prop-types';
import React from 'react';
import { isStringNumeric } from '../../../utils/formatNumber';

VMNetworkSettings.propTypes = {
  currentVM: PropTypes.object,
  updateCurrentVM: PropTypes.func
};

const minVLANTag = 1;
const maxVLANTag = 4094;
const rackNetworkPort = 'vm-p1';

// Renumbers the ports list to maintain sequential naming. Every port gets named
// `vm-p{index + 1}` based on its position. Returns the same object reference for ports that
// already have the correct name.
function renumber(ports) {
  return ports.map((port, index) => {
    const expectedName = `vm-p${index + 1}`;
    return port.name === expectedName ? port : { ...port, name: expectedName };
  });
}

// Returns the port type based on index/position
//   - first port is always (Rack Network)
//   - user selected ports that aren't the last port (when Ground Connectivity enbaled) are IFE Network
//   - The last port when Ground Connectivity is enabled, will be the Ground Port
function getNetworkTypeInfo(ports, index, connectivityOn) {
  if (connectivityOn && index === ports.length - 1) {
    return { label: 'Ground Network', color: 'info' };
  }
  return { label: 'IFE Network', color: 'success' };
}

// Builds the ports list after toggling connectivity:
//   - turning ON appends a new connectivity port (last position) and renumbers
//   - turning OFF drops the last port (the connectivity port) and renumbers
function applyConnectivityToggle(ports, turningOn) {
  if (turningOn) {
    const renumbered = renumber(ports);
    return [...renumbered, { name: `vm-p${renumbered.length + 1}` }];
  }
  return renumber(ports.slice(0, -1));
}

export function VMNetworkSettings({ currentVM, updateCurrentVM }) {
  const isVMConnectivityEnabled = useIsFeatureFlagEnabled('vm_connectivity');

  // When connectivity toggles ON, append a connectivity port at the end and renumber.
  // When it toggles OFF, drop the last port (the connectivity port) and renumber.
  const handleConnectivityChange = (event) => {
    const newConnectivity = event.target.value.toString() === 'true';
    const wasOn = Boolean(currentVM.connectivity);
    if (newConnectivity === wasOn) return;

    const ports = currentVM.network?.ports ?? [];
    const nextPorts = applyConnectivityToggle(ports, newConnectivity);
    updateCurrentVM({
      ...currentVM,
      connectivity: newConnectivity,
      network: { ...currentVM.network, ports: nextPorts }
    });
  };

  // remove the interface from the list of interfaces
  const handleRemoveNetworkInterface = (interfaceIndex) => {
    const ports = currentVM.network.ports;
    const target = ports[interfaceIndex];
    // vm-p1 is reserved and must always remain. Also refuse to remove the connectivity port
    // (the last port when connectivity is on).
    if (!target || target.name === rackNetworkPort) return;
    if (Boolean(currentVM.connectivity) && interfaceIndex === ports.length - 1) return;
    // Remove the port, then renumber to close the gap and keep port names sequential.
    const remaining = ports.filter((_, index) => index !== interfaceIndex);
    const renumbered = renumber(remaining);
    updateCurrentVM({
      ...currentVM,
      network: { ...currentVM.network, ports: renumbered }
    });
  };

  // add a new interface from the list of interfaces
  const handleAddNetworkInterface = () => {
    const ports = currentVM.network?.ports ?? [];
    const connectivityEnabled = Boolean(currentVM.connectivity);

    // Insert a new regular port. When connectivity is on, the new port goes before the
    // connectivity port so the connectivity port stays last; the whole list is renumbered
    // afterwards to keep names sequential (vm-p1, ..., vm-p{N}, vm-p{N+1} for connectivity).
    const insertedPorts = connectivityEnabled
      ? [...ports.slice(0, -1), { name: '' }, ports[ports.length - 1]]
      : [...ports, { name: '' }];
    const nextPorts = renumber(insertedPorts);

    updateCurrentVM({
      ...currentVM,
      network: { ...currentVM.network, ports: nextPorts }
    });
  };

  const handleVlanModeChange = (event, portIndex) => {
    const newVM = { ...currentVM };

    newVM.network.ports[portIndex].vlan_mode = event.target.value;
    if (event.target.value === '') {
      newVM.network.ports[portIndex] = { name: newVM.network.ports[portIndex].name };
    }

    updateCurrentVM(newVM);
  };

  const handleVlanTagChange = (event, portIndex) => {
    const vlan = event.target.value;

    if (vlan === '') {
      const newVM = { ...currentVM };
      newVM.network.ports[portIndex].tag = vlan;
      updateCurrentVM(newVM);
      return;
    }

    if (!isStringNumeric(vlan)) return;

    const vlanNumber = Number(vlan);
    if (vlanNumber < minVLANTag || vlanNumber > maxVLANTag) return;

    const newVM = { ...currentVM };
    newVM.network.ports[portIndex].tag = vlanNumber;
    updateCurrentVM(newVM);
  };

  const handleVlanTrunksChange = (trunks, portIndex) => {
    const newVM = { ...currentVM };
    const integerTrunks = trunks
      .map((trunk) => Number.parseInt(trunk, 10))
      .filter((trunk) => Number.isInteger(trunk) && trunk >= minVLANTag && trunk <= maxVLANTag);
    newVM.network.ports[portIndex].trunks = integerTrunks;
    updateCurrentVM(newVM);
  };

  return (
    <Grid item xs={12}>
      <Accordion>
        <AccordionSummary sx={{ paddingLeft: 0 }} expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Network Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack direction="column" spacing={2} data-testid="vm-network-settings">
            {isVMConnectivityEnabled && (
              <Grid container item xs={12} columnSpacing={2} alignItems="center">
                <Grid item xs={3}>
                  <FormControl>
                    <Typography variant="overline">VM Ground Connectivity</Typography>
                    <RadioGroup
                      row
                      data-testid="vm-ground-connectivity"
                      aria-label="vm-ground-connectivity"
                      value={(currentVM.connectivity ?? false).toString()}
                      onChange={handleConnectivityChange}
                    >
                      <FormControlLabel value="true" label="On" control={<Radio size="small" />} />
                      <FormControlLabel
                        value="false"
                        label="Off"
                        control={<Radio size="small" />}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
              </Grid>
            )}
            <Typography value="Network Interfaces" variant="overline">
              Network Interfaces
            </Typography>
            {currentVM.network.ports?.map((netInterface, index) => {
              // When connectivity is on, the connectivity port is always the last port.
              const isPortDisabled =
                Boolean(currentVM.connectivity) && index === currentVM.network.ports.length - 1;
              const networkType = getNetworkTypeInfo(
                currentVM.network.ports,
                index,
                Boolean(currentVM.connectivity)
              );
              return (
                <Grid container item xs={12} columnSpacing={2} key={index}>
                  <Grid item xs={3}>
                    <TextField
                      label="Port Name"
                      disabled
                      value={netInterface.name}
                      size="small"
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Chip
                              label={networkType.label}
                              color={networkType.color}
                              size="small"
                              data-testid={`network-type-${index}`}
                            />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <FormControl fullWidth size="small" disabled={isPortDisabled}>
                      <InputLabel id="vlan-mode-label">Vlan Mode</InputLabel>
                      <Select
                        labelId="vlan-mode-label"
                        data-testid="vlan-mode"
                        label="Vlan Mode"
                        fullWidth
                        size="small"
                        onChange={(event) => {
                          handleVlanModeChange(event, index);
                        }}
                        value={netInterface.vlan_mode ?? ''}
                      >
                        <MenuItem value="">Unset</MenuItem>
                        <MenuItem value="trunk">Trunk</MenuItem>
                        <MenuItem value="native-tagged">Native-Tagged</MenuItem>
                        <MenuItem value="native-untagged">Native-Untagged</MenuItem>
                        <MenuItem value="dot1q-tunnel">Dot1q-tunnel</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      inputProps={{ 'data-testid': 'vlan-field' }}
                      disabled={
                        isPortDisabled ||
                        netInterface.vlan_mode === undefined ||
                        netInterface.vlan_mode === ''
                      }
                      label="Vlan Tag"
                      size="small"
                      helperText="Allows [1,4094]"
                      onChange={(event) => {
                        handleVlanTagChange(event, index);
                      }}
                      value={currentVM.network.ports[index].tag ?? ''}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Autocomplete
                      disabled={
                        isPortDisabled ||
                        netInterface.vlan_mode === undefined ||
                        netInterface.vlan_mode === ''
                      }
                      multiple
                      options={[]}
                      freeSolo
                      onChange={(_, trunks) => {
                        handleVlanTrunksChange(trunks, index);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Vlan Trunks"
                          size="small"
                          inputProps={{
                            ...params.inputProps,
                            'data-testid': 'trunk-field',
                            onBeforeInput: (event) => {
                              const nextValue = event.data;
                              if (nextValue && !isStringNumeric(nextValue)) {
                                event.preventDefault();
                              }
                            }
                          }}
                        />
                      )}
                      value={netInterface.trunks ? netInterface.trunks.map(String) : []}
                    />
                  </Grid>
                  {currentVM.network.ports.length > 1 &&
                    !isPortDisabled &&
                    netInterface.name !== rackNetworkPort && (
                      <Grid item xs={1}>
                        <IconButton
                          data-testid="remove-interface-btn"
                          color="error"
                          onClick={() => handleRemoveNetworkInterface(index)}
                        >
                          <DeleteOutlineOutlinedIcon />
                        </IconButton>
                      </Grid>
                    )}
                </Grid>
              );
            })}
            <Button
              data-testid="add-network-interface-btn"
              startIcon={<AddIcon />}
              variant="contained"
              sx={{ maxWidth: 220 }}
              onClick={() => handleAddNetworkInterface()}
            >
              Add Network Interface
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
}
